import { useSearchViewMemory } from '../search/SearchViewMemoryContext';
import React from 'react';
import SearchBridgeRows, { bridgeSponsoredItems } from '../search/SearchBridgeRows';
import { useSponsoredPlacements } from '../ads/useSponsoredPlacements';
import { appCopy } from '../../app-ui/baseline';
import { useQuery } from '@tanstack/react-query';
import { searchApi } from '../../api/search';
import { useRecentSearches } from '../search/recentSearches';
import { useUserStore } from '../../stores/userStore';
import { useWebLocale } from '../../contexts/WebLocaleContext';
import { DiscoverySection, albumPath } from '../../components/content/DiscoveryContent';
import {
  useSearchContinuation,
  selectedArtistFor,
  selectContinuationArtist,
  matchContinuationProfile,
} from '../search/continuationArtist';
import { useNearby } from './useNearby';
export function releaseDecade(releaseDate) {
  const year = Number(releaseDate?.slice(0, 4));
  return Number.isFinite(year) && year >= 1950
    ? Math.min(2020, Math.floor(year / 10) * 10)
    : undefined;
}
export function albumRegion(album) {
  return ['KR', 'GLOBAL'].includes(album?.displayRegion)
    ? album.displayRegion
    : ['KR', 'GLOBAL'].includes(album?.originRegion)
      ? album.originRegion
      : undefined;
}
export function artistAlbumPath(artist) {
  if (artist?.slug) return `/artists/${encodeURIComponent(artist.slug)}/view/full-albums`;
  const sourceId = artist?.sourceId || artist?.id;
  return sourceId
    ? `/artists/${String(artist.artistSource || 'APPLE_MUSIC').toLowerCase()}/${encodeURIComponent(sourceId)}/full-albums`
    : null;
}
function useBridgeQuery(term, enabled, userId, locale) {
  return useQuery({
    queryKey: ['web-search-bridge', userId || 'guest', locale, term],
    enabled: enabled && Boolean(term),
    staleTime: 600000,
    retry: false,
    queryFn: async ({ signal }) =>
      (
        await searchApi.searchSummary(term, {
          signal,
          authenticated: Boolean(userId),
          params: { locale },
        })
      ).data,
  });
}
export default function SearchBridges({ data, home }) {
  const memory = useSearchViewMemory();
  const { locale } = useWebLocale();
  const t = (key, params) => appCopy(locale, key, params);
  const campaigns = useSponsoredPlacements('SEARCH_CONTINUE');
  const userId = useUserStore((state) => state.user?.id);
  const [ref, nearby] = useNearby();
  const recent = useRecentSearches();
  const term = recent[0] || '';
  const randomTerm = memory.choose(
    'bridge-term',
    [JSON.stringify(recent)],
    () => recent[Math.floor(Math.random() * recent.length)] || '',
  );
  const summary = useBridgeQuery(term, nearby, userId, locale);
  const regionSummary = useBridgeQuery(randomTerm, nearby, userId, locale);
  const hasPersonal = Boolean(userId) && data?.personalizationLevel !== 'GENERAL';
  const personal = hasPersonal ? data?.albums || [] : [];
  const selection = useSearchContinuation((state) => state.selection);
  const identity = selectContinuationArtist({
    query: term,
    selected: selectedArtistFor(selection, userId, term),
    results: summary.data,
    fallback: personal[0]?.artist,
  });
  const name = identity?.name;
  const profileQuery = useBridgeQuery(name, nearby, userId, locale);
  const profile = matchContinuationProfile(identity, profileQuery.data?.artists?.items || []);
  const artist = identity
    ? {
        ...identity,
        id: undefined, // identity.id is a local DB ID, never a provider route ID.
        artistId: identity.id ?? profile?.artistId,
        slug: identity.slug ?? (profile?.artistId != null ? profile.slug : undefined),
        sourceId: identity.sourceId ?? (profile?.id == null ? undefined : String(profile.id)),
        artistSource: identity.artistSource ?? profile?.artistSource,
        imageUrl: identity.imageUrl ?? profile?.artistImageUrl ?? profile?.imageUrl,
      }
    : undefined;
  const path = artistAlbumPath(artist);
  const regionAlbum = memory.choose('bridge-region', [randomTerm, regionSummary.data, name], () => {
    const candidates = (regionSummary.data?.albums?.items || []).filter(
      (item) =>
        releaseDecade(item.releaseDate) &&
        Boolean(albumRegion(item)) &&
        item.artist?.name?.toLocaleLowerCase() !== name?.toLocaleLowerCase(),
    );
    return candidates[Math.floor(Math.random() * candidates.length)];
  });
  const region =
    regionAlbum ||
    personal.find(
      (item) =>
        releaseDecade(item.releaseDate) &&
        ['KR', 'GLOBAL'].includes(item.displayRegion) &&
        item.artist?.name?.toLocaleLowerCase() !== name?.toLocaleLowerCase(),
    );
  const decade = releaseDecade(region?.releaseDate);
  const items = [];
  if (name && path)
    items.push({
      id: `artist-${name}`,
      title: t('search.moreArtistAlbums', { artist: name }),
      meta: t('search.artistAlbums'),
      image: artist?.artistImageUrl || artist?.imageUrl,
      to: path,
    });
  if (region && decade)
    items.push({
      id: regionAlbum ? `region-${region.sourceId}` : `personalized-region-${region.id}`,
      title: t('search.browseEraRegion', {
        decade: locale === 'ko' ? `${String(decade).slice(2)}년대` : `${decade}s`,
        region: t(albumRegion(region) === 'KR' ? 'search.region.kr' : 'search.region.global'),
      }),
      meta: t(regionAlbum ? 'search.sameEraAsRecent' : 'search.sameEraAsInterest'),
      image: region.albumImageUrl || region.imageUrl,
      to: `/explore/timeline?decade=${decade}`,
    });
  else
    items.push({
      id: 'fallback-timeline',
      title: t('search.browseByEra'),
      meta: t('search.allEras'),
      to: '/explore/timeline',
    });
  if (items.length < 2 && hasPersonal) {
    const album = personal[1] || personal[0] || home?.recommendedReviews?.[0];
    if (album)
      items.push({
        id: `fallback-editorial-${album.id ?? album.albumId}`,
        title: t('search.browsePostpopPicks'),
        meta: t('search.findFromEditorial'),
        image: album.imageUrl || album.albumImageUrl,
        to: albumPath(album),
      });
  }
  if (items.length < 2)
    items.push({
      id: 'fallback-reviews',
      title: t('search.findAlbumFromReviews'),
      meta: t('search.continueFromFanRecords'),
      to: '/reviews',
    });
  return (
    <div ref={ref}>
      <DiscoverySection
        title={t(recent.length ? 'search.continueDiscovering' : 'search.startExploring')}
      >
        <SearchBridgeRows items={bridgeSponsoredItems(items, campaigns, t('common.artist'))} />
      </DiscoverySection>
    </div>
  );
}
